import * as React from 'react'

import {
  Box,
  Icon,
  Tag,
  TagColor,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { Currency } from 'dinero.js'
import moment from 'moment'

import { observer } from 'mobx-react'

import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import { resultConceptsLabels } from 'modules/payroll_bundle/lib/constants'
import type { ResultConceptsLabelsType } from 'modules/payroll_bundle/lib/constants'
import { formatAmount } from 'modules/payroll_bundle/lib/formatNumber'
import type Payment from 'modules/payroll_bundle/store/models/Payment'

import ActionsPopover from './ActionsPopover'

interface Props {
  payment: Payment
  currency: Currency
}

const Item = ({ payment, currency }: Props) => {
  const [open, toggle] = useToggle(false)
  const author = payment.author
  const concept: string = payment.attributes.concept
  const createdAtText = moment(payment.attributes.created_at).format('MMM D h:mm a')
  const titleText = i18n.t('payroll_bundle.results_payments.payments.list.new_title', {
    concept: resultConceptsLabels()[concept as ResultConceptsLabelsType],
    createdAtText,
  })

  const COLOR_BY_STATUS = {
    pending: TagColor.champagne,
    paid: TagColor.lime,
  } as const

  const status =
    payment.attributes.status === 'paid'
      ? i18n.t('payroll_bundle.statuses.paid')
      : i18n.t('payroll_bundle.statuses.pending')

  return (
    <Box
      gap="s8"
      padding="s16"
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      borderRadius={{
        all: 'abs008',
      }}
      aria-label="payment item"
    >
      <Box alignItems="center" justifyContent="spaceBetween" width="full" flexDirection="row">
        <Box gap="s8" flexDirection="column" flexGrow minWidth="fit-content">
          <Box alignItems="center" flexDirection="row">
            <Text.Normal overflowWrap size="300" weight="semibold">
              {titleText}
            </Text.Normal>
          </Box>
          {author && (
            <Text.Normal size="200">
              {i18n.t('payroll_bundle.results_payments.payments.list.ordered_by', {
                authorName: author.fullName(),
              })}
            </Text.Normal>
          )}
        </Box>
        <Box justifyContent="flexStart" width="s200">
          <Text.Normal>
            {i18n.tView('payroll_bundle.results_payments.payments.list.total_amount', {
              bold_text: <strong>{formatAmount(payment.paymentsTotalAmount, currency)}</strong>,
            })}
          </Text.Normal>
        </Box>
        <Box justifyContent="flexStart" width="s200">
          <Text.Normal>
            {i18n.tView('payroll_bundle.results_payments.payments.list.total_employee', {
              bold_text: <strong>{payment.totalEmployees}</strong>,
            })}
          </Text.Normal>
        </Box>
        <Box flexDirection="row" alignItems="center" gap="s40">
          <Box justifyContent="flexStart" flexDirection="row" width="s120">
            <Box alignSelf="center">
              <Tag
                type={COLOR_BY_STATUS[payment.attributes.status as keyof typeof COLOR_BY_STATUS]}
              >
                {status}
              </Tag>
            </Box>
            {payment.attributes.status === 'pending' && (
              <Box paddingY="s16" paddingX="s8">
                <Tooltip.CallToAction
                  attachment="bottom center"
                  onClick={() => window.open(helpCenterUrl('payroll_bundle'))}
                  description={i18n.t(
                    'payroll_bundle.results_payments.payments.payment_tooltip.description'
                  )}
                  title={i18n.t('payroll_bundle.results_payments.payments.payment_tooltip.button')}
                  buttonLabel={i18n.t(
                    'payroll_bundle.results_payments.payments.payment_tooltip.button'
                  )}
                  textAlign="left"
                  open={open}
                  toggle={toggle}
                >
                  <Icon.Medium icon="info-circle-line" color="grey800" />
                </Tooltip.CallToAction>
              </Box>
            )}
          </Box>
          <Box width="fit-content">
            <ActionsPopover payment={payment} />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default observer(Item)
